/**
 * Loader bridge — the app's side of the `postMessage` protocol it speaks with the embed
 * loader running in the HOST page (`src/loader/loader.ts`, ARCHITECTURE §7).
 *
 * Two jobs:
 *
 * **1. One outbound door.** Every app→loader message (`expand` / `collapse` / `hide` /
 * `position` / `hello`) goes through {@link postToLoader}, so the envelope and the
 * "not embedded" fallback are written once.
 *
 * **2. The capability handshake.** `loader.js` is the one file we cannot version with the
 * app: customers paste it by a stable URL and it is served cacheable, so a *browser* holds
 * a copy for hours — a CDN purge does not reach into a visitor's cache. For that window
 * after every deploy, the NEW app runs against an OLD loader.
 *
 * That asymmetry is only dangerous in one direction. The collapsed frame is a transparent
 * 96×96 box pinned over the customer's own page, and `hide` is the message that takes it
 * out of hit-testing. An old loader has no `hide` case and ignores it. So an app that
 * offered the launcher's dismiss × on faith would produce, on a visitor's tap: app renders
 * nothing, loader keeps the box → an invisible click-eating dead zone in the corner of
 * someone's website, for as long as their cached loader lives.
 *
 * So the app assumes nothing. The loader announces what it understands, and the × is
 * offered only once `hide` has actually been heard. An old loader stays silent, the ×
 * never renders, and a stored dismissal is ignored — the feature is simply *absent* for
 * those visitors instead of broken. Degrading to "missing" is always available to us;
 * degrading to "dead zone" is not.
 */

import { APP_SOURCE, isLoaderSource } from './loaderProtocol';

/** Everything the app can ask the loader to do. `hello` is the handshake probe. */
export type LoaderMessage =
  | { type: 'expand' }
  | { type: 'collapse'; teaser?: boolean }
  | { type: 'hide' }
  | { type: 'dock' }
  | { type: 'hello' }
  | {
      type: 'position';
      corner: 'bottom-right' | 'bottom-left';
      side?: number;
      bottom?: number;
    };

/**
 * Post a message to the loader in the parent frame.
 *
 * `'*'` as the target origin matches the rest of this protocol (cobrowse) and is safe
 * here: these messages carry frame geometry, nothing about the visitor. The loader is
 * the party that must be careful — it pins the accepted origin to the app's own.
 */
export function postToLoader(message: LoaderMessage): void {
  try {
    window.parent?.postMessage({ source: APP_SOURCE, ...message }, '*');
  } catch {
    // Not embedded (standalone dev harness) — no parent to notify.
  }
}

/**
 * What we know about the loader on the other side of this frame:
 *
 * - `unknown` — handshake still in flight; only ever the first moments of a page load.
 * - `yes` — the loader announced `dock`; dismissal is safe to offer. `dock` (not `hide`)
 *   is the gate on purpose: dismissing now leaves a small restore arrow behind, so the ×
 *   is only safe when the loader can shrink the frame to it. A loader that knows only
 *   `hide` (the first-generation handshake build) would take the frame off the page
 *   entirely — stranding the arrow, which is the visitor's only way back.
 * - `no` — nothing announced in {@link HANDSHAKE_TIMEOUT_MS}: assume a cached old loader.
 *   Not terminal — a late announcement still upgrades us, so a wedged host page costs the
 *   feature for a moment, not for the page's life.
 */
export type LoaderSupport = 'unknown' | 'yes' | 'no';

/**
 * The first probe goes out the instant this module is imported. It cannot be *too* early:
 * the loader installs its `message` listener in the same synchronous block that creates
 * our iframe, so the listener always exists before our document has even been fetched.
 * The retries below exist for the other hazard — a host page whose main thread is busy
 * when we ask, since the reply is dispatched on ITS event loop, not ours.
 */
const PROBE_RETRY_MS = [200, 700];

/** Stop waiting and assume an old loader. Generous: the round trip is normally one task. */
const HANDSHAKE_TIMEOUT_MS = 1500;

let hideSupport: LoaderSupport = 'unknown';
const listeners = new Set<() => void>();

/** `yes` is sticky — a capability the loader has announced cannot be taken back by a timer. */
function resolveSupport(next: LoaderSupport): void {
  if (hideSupport === next || hideSupport === 'yes') return;
  hideSupport = next;
  listeners.forEach((l) => l());
}

/** `useSyncExternalStore` subscribe — the × must appear when support lands, not at mount. */
export function subscribeLoaderSupport(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `useSyncExternalStore` snapshot. A primitive, so React never sees a changed identity. */
export function getHideSupport(): LoaderSupport {
  return hideSupport;
}

/** Are we inside someone's page, or opened directly (dev harness)? */
function embedded(): boolean {
  try {
    return window.parent !== window;
  } catch {
    // Cannot tell — answer conservatively. "Embedded" keeps the × behind the handshake
    // instead of handing it out for free.
    return true;
  }
}

/**
 * Accept a capability announcement — but only from the frame that actually owns our wrapper.
 *
 * `e.origin` is useless as a filter here: the loader runs on the CUSTOMER's page, so every
 * embed announces from a different origin and there is no list to check against. The parent
 * `WindowProxy` identity is the real check. An opener, a sibling frame, a popup or any other
 * window on the page can post to us and all of them fail it; the only window that passes is
 * the one that already controls our frame's size and position, and therefore has nothing to
 * gain by lying to us about it.
 */
function onLoaderMessage(e: MessageEvent): void {
  if (e.source !== window.parent) return;
  const data = e.data as { source?: string; type?: string; features?: unknown } | undefined;
  if (!data || !isLoaderSource(data.source) || data.type !== 'capabilities') return;
  const features = Array.isArray(data.features) ? (data.features as unknown[]) : [];
  if (features.includes('dock')) resolveSupport('yes');
}

function beginHandshake(): void {
  if (!embedded()) {
    // Standalone dev harness / the app opened directly: there is no loader — and no host
    // page whose corner could be stranded, because the widget IS the document. Report
    // support so the dismiss UI is reachable while working on it. `postToLoader` is a
    // no-op here, so dismissing just empties the page, and a new tab brings it back
    // (sessionStorage), exactly as in production.
    resolveSupport('yes');
    return;
  }
  window.addEventListener('message', onLoaderMessage);
  postToLoader({ type: 'hello' });
  for (const delay of PROBE_RETRY_MS) {
    window.setTimeout(() => {
      if (hideSupport !== 'yes') postToLoader({ type: 'hello' });
    }, delay);
  }
  window.setTimeout(() => resolveSupport('no'), HANDSHAKE_TIMEOUT_MS);
}

beginHandshake();
